import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Upload, Loader2, Clock, CheckCircle2, XCircle, CalendarCheck, Trash2, Radio, BarChart3, Image as ImageIcon, Link as LinkIcon } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

const STATUS = {
  awaiting_payment: { label: "Awaiting payment", cls: "text-zinc-400 bg-white/5", icon: Clock },
  awaiting_upload: { label: "Ready to upload", cls: "text-[#E6B34A] bg-[#E6B34A]/10", icon: Upload },
  pending_review: { label: "Under review", cls: "text-[#5B7CFA] bg-[#5B7CFA]/10", icon: Clock },
  approved: { label: "Approved", cls: "text-emerald-400 bg-emerald-400/10", icon: CheckCircle2 },
  scheduled: { label: "Scheduled · On air", cls: "text-emerald-400 bg-emerald-400/10", icon: CalendarCheck },
  rejected: { label: "Rejected", cls: "text-[#FF3B30] bg-[#FF3B30]/10", icon: XCircle },
};

export function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.awaiting_payment;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 overline px-2.5 py-1 rounded-full ${s.cls}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

export function SpotAudio({ spot }) {
  const token = localStorage.getItem("token");
  if (!spot.audio_url) return null;
  return (
    <audio controls className="w-full mt-3 h-10" data-testid={`audio-${spot.id}`}>
      <source src={`${API}/spots/${spot.id}/audio?auth=${token}`} />
    </audio>
  );
}

export default function MySpots() {
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadingLogoId, setUploadingLogoId] = useState(null);
  const [linkDrafts, setLinkDrafts] = useState({});
  const fileRefs = useRef({});
  const logoRefs = useRef({});
  const token = localStorage.getItem("token");

  const load = () => api.get("/spots").then((res) => { setSpots(res.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const onUpload = async (spot, file) => {
    if (!file) return;
    setUploadingId(spot.id);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/spots/${spot.id}/upload`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Audio uploaded! It's now under review.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploadingId(null); }
  };

  const onLogoUpload = async (spot, file) => {
    if (!file) return;
    setUploadingLogoId(spot.id);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/spots/${spot.id}/logo`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sponsor logo saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Logo upload failed");
    } finally { setUploadingLogoId(null); }
  };

  const saveLink = async (spot) => {
    const link_url = linkDrafts[spot.id] ?? spot.link_url ?? "";
    try {
      await api.put(`/spots/${spot.id}/link`, { link_url });
      toast.success("Sponsor link saved");
      load();
    } catch (e) { toast.error("Could not save link"); }
  };

  const remove = async (id) => {
    await api.delete(`/spots/${id}`);
    toast.success("Spot removed");
    load();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-10">
      <div className="flex items-center gap-3 mb-1">
        <Megaphone className="w-5 h-5 text-[#E6B34A]" />
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">My Ad Spots</h1>
      </div>
      <p className="text-zinc-400 mb-8">Upload your audio and track your campaigns. <Link to="/advertise" className="text-[#E6B34A] font-semibold" data-testid="buy-more">Buy another spot →</Link></p>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#E6B34A]" /></div>
      ) : spots.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-white/10 rounded-2xl" data-testid="spots-empty">
          <Megaphone className="w-10 h-10 mx-auto text-zinc-700 mb-4" />
          <p className="text-zinc-400 mb-4">No ad spots yet.</p>
          <Link to="/advertise" className="inline-block px-5 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold">Browse packages</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5" data-testid="my-spots-list">
          {spots.map((spot) => (
            <div key={spot.id} className="rounded-2xl border border-white/5 bg-[#141C42] p-6" data-testid={`spot-${spot.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-display font-extrabold text-xl tracking-tight truncate">{spot.business_name}</h3>
                  <p className="text-sm text-zinc-500 mt-0.5">{spot.package_name} · {spot.duration}s · {spot.plays_purchased} plays</p>
                </div>
                <StatusBadge status={spot.status} />
              </div>

              {spot.status === "rejected" && spot.rejection_reason && (
                <p className="text-sm text-[#FF3B30] mt-3 bg-[#FF3B30]/10 rounded-lg px-3 py-2">Reason: {spot.rejection_reason}</p>
              )}
              {spot.program_title && (
                <p className="text-sm text-emerald-400 mt-3 flex items-center gap-1.5"><Radio className="w-4 h-4" /> Airing during "{spot.program_title}"</p>
              )}

              {spot.plays_purchased > 0 && ["approved", "scheduled"].includes(spot.status) && (
                <div className="mt-4" data-testid={`analytics-${spot.id}`}>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-zinc-400 flex items-center gap-1"><BarChart3 className="w-3.5 h-3.5" /> Plays aired</span>
                    <span className="font-semibold" data-testid={`plays-${spot.id}`}>{spot.plays_aired} / {spot.plays_purchased}</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-[#E6B34A] rounded-full" style={{ width: `${Math.min(100, (spot.plays_aired / spot.plays_purchased) * 100)}%`, transition: "width 0.4s ease" }} />
                  </div>
                  {spot.status === "scheduled" && (
                    <p className="text-xs text-emerald-400 mt-1.5 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 live-dot" /> {spot.plays_remaining} plays remaining · airing now</p>
                  )}
                </div>
              )}

              <SpotAudio spot={spot} />

              <div className="mt-4 flex items-center gap-3" data-testid={`logo-row-${spot.id}`}>
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  {spot.has_logo ? (
                    <img src={`${API}/spots/${spot.id}/logo?auth=${token}`} alt="Sponsor logo" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-4 h-4 text-zinc-600" />
                  )}
                </div>
                <input
                  ref={(el) => (logoRefs.current[spot.id] = el)}
                  type="file" accept="image/*" className="hidden"
                  data-testid={`logo-file-${spot.id}`}
                  onChange={(e) => onLogoUpload(spot, e.target.files[0])}
                />
                <button
                  onClick={() => logoRefs.current[spot.id]?.click()}
                  disabled={uploadingLogoId === spot.id}
                  data-testid={`logo-upload-${spot.id}`}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-300 hover:text-white transition-colors disabled:opacity-60"
                >
                  {uploadingLogoId === spot.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                  {spot.has_logo ? "Change sponsor logo" : "Add sponsor logo"}
                </button>
              </div>

              <div className="mt-3 flex items-center gap-2" data-testid={`link-row-${spot.id}`}>
                <LinkIcon className="w-4 h-4 text-zinc-500 shrink-0" />
                <input
                  value={linkDrafts[spot.id] ?? spot.link_url ?? ""}
                  onChange={(e) => setLinkDrafts({ ...linkDrafts, [spot.id]: e.target.value })}
                  placeholder="https://your-website.com"
                  data-testid={`link-input-${spot.id}`}
                  className="flex-1 min-w-0 rounded-lg bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]"
                />
                <button onClick={() => saveLink(spot)} data-testid={`link-save-${spot.id}`} className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-semibold transition-colors shrink-0">Save</button>
              </div>

              <div className="mt-4 flex items-center gap-3">
                {(spot.status === "awaiting_upload" || spot.status === "rejected") && (
                  <>
                    <input
                      ref={(el) => (fileRefs.current[spot.id] = el)}
                      type="file" accept="audio/*" className="hidden"
                      data-testid={`file-${spot.id}`}
                      onChange={(e) => onUpload(spot, e.target.files[0])}
                    />
                    <button
                      onClick={() => fileRefs.current[spot.id]?.click()}
                      disabled={uploadingId === spot.id}
                      data-testid={`upload-${spot.id}`}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold text-sm hover:bg-[#CF9A34] transition-colors disabled:opacity-60"
                    >
                      {uploadingId === spot.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {spot.status === "rejected" ? "Re-upload audio" : "Upload audio ad"}
                    </button>
                  </>
                )}
                {spot.original_filename && <span className="text-xs text-zinc-500 truncate">{spot.original_filename}</span>}
                <button onClick={() => remove(spot.id)} data-testid={`del-spot-${spot.id}`} className="ml-auto p-2 rounded-lg hover:bg-[#FF3B30]/10 text-zinc-500 hover:text-[#FF3B30] transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
